/**
 * Barra fija que avisa de que estás dentro de la cuenta de un cliente.
 *
 * Se monta en `App.jsx` y no en `Layout.jsx` a propósito: hay páginas que se
 * renderizan fuera del Layout, y precisamente en esas es donde más fácil sería
 * olvidar que no estás en tu propia cuenta.
 *
 * La cuenta atrás no es decorativa: la sesión de soporte caduca en el backend
 * (`IMPERSONATION_MAX_SECONDS`) de forma perezosa, así que sin este aviso el
 * agente vería de pronto la app como su admin sin entender por qué.
 *
 * **El interruptor de modo fiel vive aquí** y no en un ajuste escondido, porque
 * cambia lo que significa TODO lo que se ve debajo. Por defecto el agente
 * conserva sus superpoderes —no gasta cuota del cliente y ningún gate de plan
 * le frena—, y con «ver como él» la aplicación se comporta exactamente como
 * para el cliente. Sin ese modo no se puede reproducir el problema de nadie,
 * que es para lo que existe entrar en su cuenta.
 *
 * La barra CAMBIA DE COLOR con el modo, y no es decoración: en modo fiel el
 * agente ve gates y saldos reales, así que necesita saber de un vistazo si lo
 * que tiene delante es la vista del cliente o la suya. Ámbar = superpoderes,
 * añil = lo que ve él.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Handshake, Loader2, LogOut, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';

import { getImpersonationState } from '@/api/supportAccessService';
import { useLanguage } from '@/components/LanguageContext';
import { useAuth } from '@/lib/AuthContext';

// Cada cuánto vigila la barra una sesión CON PERMISO. Por debajo del latido
// del backend (`SUPPORT_ACCESS_ADMIN_HEARTBEAT_SECONDS`, 90 s): esta consulta
// es lo que mantiene viva la sesión aunque el agente esté leyendo sin tocar
// nada, y lo que le saca con un aviso si el usuario le retira el acceso.
export const CONSENTED_POLL_MS = 15000;

/** Solo en sesiones consentidas. Si el backend dice que ya no hay sesión, se
 *  avisa y se sale con el vaciado de caché de siempre. */
function useConsentedSessionWatch(active, t, leaveImpersonation) {
  const { data } = useQuery({
    queryKey: ['impersonation', 'state'],
    queryFn: getImpersonationState,
    enabled: active,
    refetchInterval: CONSENTED_POLL_MS,
    // También con la pestaña oculta: es el latido de la sesión.
    refetchIntervalInBackground: true,
    retry: false,
  });
  // Una sola salida: `leaveImpersonation` es una función nueva en cada render y
  // la recarga no es instantánea, así que sin esto el aviso podría repetirse.
  const leaving = useRef(false);
  useEffect(() => {
    if (!active || !data || data.active || leaving.current) return;
    leaving.current = true;
    toast(t(data.endReason === 'revoked'
      ? 'supportAccess.admin.revokedToast'
      : 'supportAccess.admin.sessionEndedToast'), { icon: '🔒', duration: 6000 });
    leaveImpersonation();
  }, [active, data, t, leaveImpersonation]);
}

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const target = new Date(expiresAt).getTime();
    if (Number.isNaN(target)) return undefined;
    const tick = () => setRemaining(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (remaining === null) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function ImpersonationBar() {
  const { t } = useLanguage();
  const {
    impersonation, stopImpersonation, setImpersonationViewMode, leaveImpersonation,
  } = useAuth();
  // Un solo cerrojo para los dos botones: ambos terminan recargando la página,
  // y un segundo clic mientras el primero vuela dejaría la caché vaciada contra
  // el modo equivocado.
  const [busy, setBusy] = useState(false);
  const countdown = useCountdown(impersonation?.expiresAt);
  const consented = !!(impersonation?.active && impersonation?.consented);
  useConsentedSessionWatch(consented, t, leaveImpersonation);

  if (!impersonation?.active) return null;

  const fiel = !!impersonation.viewAsCustomer;

  const run = (action) => async () => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={`fixed top-0 inset-x-0 z-[100] shadow-md ${
        fiel ? 'bg-indigo-600 text-white' : 'bg-amber-500 text-black'
      }`}>
        <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-3 text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span className="font-semibold truncate">
            {t(fiel ? 'impersonation.bannerTitleAsCustomer' : 'impersonation.bannerTitle')}
          </span>
          <span className="truncate opacity-90">{impersonation.targetEmail}</span>
          {consented && (
            <span
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-black/15"
              title={t('supportAccess.admin.consentedHint')}
            >
              <Handshake className="w-3 h-3" />
              {t('supportAccess.admin.consentedBadge')}
            </span>
          )}
          {countdown && (
            <span className="ml-auto tabular-nums text-xs opacity-80 shrink-0">
              {t('impersonation.expiresIn')} {countdown}
            </span>
          )}
          <button
            type="button"
            onClick={run(() => setImpersonationViewMode(!fiel))}
            disabled={busy}
            title={t('impersonation.viewAsHint')}
            className={`${countdown ? '' : 'ml-auto'} shrink-0 inline-flex items-center gap-1.5 font-semibold text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-60 ${
              fiel
                ? 'bg-white/20 hover:bg-white/30 text-white'
                : 'bg-black/10 hover:bg-black/20 text-black'
            }`}
          >
            {busy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : (fiel ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />)}
            {t(fiel ? 'impersonation.backToAdminView' : 'impersonation.viewAsCustomer')}
          </button>
          <button
            type="button"
            onClick={run(stopImpersonation)}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1.5 bg-black/85 hover:bg-black text-white font-semibold text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t('impersonation.exit')}
          </button>
        </div>
      </div>
      {/* Espaciador: la barra es `fixed`, así que sin esto taparía la cabecera
          de cada página. */}
      <div className="h-11" aria-hidden="true" />
    </>
  );
}
